import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { MiLogoMark } from "@/components/MiLogoMark";
import { ArrowRight } from "lucide-react";
import miLogo from "@/assets/mi-support-logo-white.svg";
import heroVideo from "@/assets/misupport-hero-video.mp4.asset.json";
import { Img, SIZES } from "@/components/Img";
import kontorGruppe42 from "@/assets/kontor-gruppe-4-2.jpg";
import tavle22 from "@/assets/tavle2-2.jpg";
import julieKontor3 from "@/assets/julie-kontor3.jpg";
import kontorGruppe1 from "@/assets/kontor-gruppe-1.jpg";
import digitalRealty from "@/assets/partners/digital-realty.png.asset.json";
import siemens from "@/assets/partners/siemens.svg.asset.json";
import { type Locale } from "@/i18n/config";
import { pathFor, type PageId } from "@/i18n/slugMap";
import { homeStrings } from "@/i18n/content/home";
import { postsForLocale, articlePath } from "@/data/blog/posts";
import { pageHead, BASE_URL } from "@/i18n/head";
import { videoObject } from "@/lib/schema";

const EMPLY_POSITIONS = "https://misupport.career.emply.com/da/ledige-stillinger";

/** <head> for the home page: pageHead + VideoObject for the hero video, so
 *  Google's video indexing gets the thumbnailUrl/uploadDate it requires. */
export function homeHead(locale: Locale) {
  const t = homeStrings[locale];
  return pageHead("home", locale, t.meta, {
    schema: [
      videoObject({
        name: t.hero.videoTitle,
        description: t.meta.description,
        contentUrl: BASE_URL + heroVideo.url,
        thumbnailUrl: BASE_URL + "/wp-content/uploads/hero-video-thumb.jpg",
        uploadDate: "2026-06-17",
        duration: "PT11S",
      }),
    ],
  });
}

// Locale-independent metadata, aligned by index with homeStrings.serviceCards.
const serviceCardMeta: ({ img: string } & ({ id: PageId } | { external: string }))[] = [
  { img: kontorGruppe42, id: "projektledelse" },
  { img: "/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg", id: "byggeledelse" },
  { img: "/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg", id: "bygherreraadgivning" },
  { img: kontorGruppe1, id: "hse" },
  { img: tavle22, id: "tidsplanlaegning" },
  { img: "/wp-content/uploads/MISupportIT_NTK3754-scaled.jpg", id: "projektoekonomi" },
  { img: julieKontor3, id: "projektassistance" },
  { img: "/wp-content/uploads/Dan-Container4_1400x1400-1.jpg", id: "shutdown" },
  { img: "/assets-photos/digitale-vaerktoejer.jpg", external: "https://sitesupport.dk/" },
];

// w/h: only needed for SVG logos — they are not in imageManifest, so <Img>
// can't derive intrinsic dimensions (raster logos get them from the manifest).
const partners: { name: string; img: string; w?: number; h?: number }[] = [
  { name: "Novo Nordisk", img: "/wp-content/uploads/Novo-Nordisk_300x200.png" },
  { name: "NNE", img: "/wp-content/uploads/NNE_300x200.png" },
  { name: "Novonesis", img: "/wp-content/uploads/Novonesis_300x200.jpg" },
  { name: "Ørsted", img: "/wp-content/uploads/Orsted_300x200.png" },
  { name: "Digital Realty", img: digitalRealty.url },
  { name: "CPH Airports", img: "/wp-content/uploads/CPH_300x200.png" },
  { name: "Siemens", img: siemens.url, w: 1000, h: 159 },
  { name: "Carlsberg", img: "/wp-content/uploads/Carlsberg_300x200.png" },
];

const team = [
  { name: "Sonny Christensen", img: "/wp-content/uploads/Sonny_portraet_400x527.jpg" },
  { name: "Jonas Terkild Christensen", img: "/wp-content/uploads/Jonas_portraet_400x527.jpg" },
  { name: "Simon Terkild Christensen", img: "/wp-content/uploads/Simon_portraet_400x527.jpg" },
  { name: "Lars Christiansen", img: "/assets-photos/lars-christiansen.png" },
];

export function HomePage({ locale }: { locale: Locale }) {
  const t = homeStrings[locale];
  const latestPosts = postsForLocale(locale).slice(0, 2);

  return (
    <SiteLayout>
      {/* Hero — the autoplay video is heavy (~3.5MB), so it only loads on md+.
          On mobile the video + overlay are hidden and preload="none" means the
          browser never fetches the mp4; a branded blue gradient backs the hero
          instead so the white logo/text stay readable. */}
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary to-background">
        <video
          className="hidden md:block absolute inset-0 w-full h-full object-cover"
          src={heroVideo.url}
          poster="/wp-content/uploads/hero-video-thumb.jpg"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          aria-hidden="true"
        />
        <div className="hidden md:block absolute inset-0 bg-background/50" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 md:px-8 pt-24 pb-28 md:pt-32 md:pb-36 text-center">
          <h1>
            <span className="sr-only">{t.hero.srTitle}</span>
            <img
              src={miLogo}
              alt="MI Support"
              width={498}
              height={104}
              className="mx-auto w-full max-w-md md:max-w-lg h-auto"
            />
          </h1>
          <p className="mt-10 text-xl md:text-2xl text-foreground/95">{t.hero.tagline}</p>
          <p className="mt-6 text-base md:text-lg text-foreground/85 leading-relaxed max-w-3xl mx-auto">
            {t.hero.body}
          </p>
          <Link to={pathFor("kontakt", locale)} className="mt-10 btn-cta">
            {t.hero.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Er du vores nye kollega? */}
      <section className="bg-secondary text-foreground">
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-20 grid gap-12 lg:grid-cols-2 items-center">
          <Img
            src="/assets-photos/kollega-NTK4170.jpg"
            alt={t.kollega.heading}
            className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/3]"
            sizes={SIZES.split}
          />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.kollega.heading}</h2>
            <p className="mt-6 leading-relaxed">{t.kollega.body1}</p>
            <p className="mt-6 leading-relaxed">{t.kollega.body2}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={pathFor("arbejdsplads", locale)}
                className="group inline-flex items-center rounded-2xl border-2 border-white bg-transparent px-9 py-4 text-base font-semibold text-white transition-colors hover:bg-white hover:text-secondary"
              >
                <MiLogoMark aria-label="MI Support" className="h-4 w-[77px] mr-2 translate-y-[2px]" />
                <span>{t.kollega.workplaceBtn}</span>
              </Link>
              <a href={EMPLY_POSITIONS} className="btn-cta">
                {t.kollega.positionsBtn} <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ydelseskort */}
      <section className="py-24 bg-section-light">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center text-section-light-foreground">
            {t.servicesSection.heading}
          </h2>
          <p className="mt-6 text-center text-section-light-foreground/80 max-w-3xl mx-auto leading-relaxed">
            {t.servicesSection.intro}
          </p>
          <div className="mt-12 grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {serviceCardMeta.map((card, i) => {
              const s = t.serviceCards[i];
              const cta = (
                <>
                  {t.servicesSection.readMore}
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              );
              return (
                <article
                  key={s.title}
                  className="group flex flex-col bg-secondary rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/40 hover:-translate-y-1"
                >
                  <div className="relative shrink-0 overflow-hidden">
                    <Img
                      src={card.img}
                      alt={s.title}
                      sizes={SIZES.card3}
                      className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent" />
                  </div>
                  <div className="p-5 flex flex-col justify-between flex-1 min-w-0">
                    <div>
                      <h3 className="text-foreground font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                        {s.title}
                      </h3>
                      <p className="text-foreground/80 text-sm leading-relaxed line-clamp-3">{s.text}</p>
                    </div>
                    {"external" in card ? (
                      <a
                        href={card.external}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center text-foreground/60 text-xs font-semibold uppercase tracking-wider group-hover:text-foreground transition-colors"
                      >
                        {cta}
                      </a>
                    ) : (
                      <Link
                        to={pathFor(card.id, locale)}
                        className="mt-4 inline-flex items-center text-foreground/60 text-xs font-semibold uppercase tracking-wider group-hover:text-foreground transition-colors"
                      >
                        {cta}
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Alle faser */}
      <section className="bg-background text-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.faser.heading}</h2>
            <div className="mt-6 text-foreground/85 leading-relaxed space-y-4">
              {t.faser.paras.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4261_1350x900.jpg"
            alt={t.faser.heading}
            className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/3]"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Samarbejdspartnere */}
      <section className="bg-white text-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-section-light-foreground">
            {t.partnersSection.heading}
          </h2>
          <p className="mt-6 max-w-3xl mx-auto text-section-light-foreground leading-relaxed">
            {t.partnersSection.body}
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            {partners.map((p) => (
              <Img key={p.name} src={p.img} alt={p.name} width={p.w} height={p.h} className="mx-auto w-[250px] h-auto object-contain" sizes={SIZES.logo} />
            ))}
          </div>
          <Link to={pathFor("samarbejdspartnere", locale)} className="mt-12 btn-cta">
            {t.partnersSection.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Din sikkerhed / garanti */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK3754-scaled.jpg"
            alt={t.garanti.heading}
            className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/3]"
            sizes={SIZES.split}
          />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.garanti.heading}</h2>
            <p className="mt-6 text-foreground/85 leading-relaxed">{t.garanti.para1}</p>
            <p className="mt-4 text-foreground/85 leading-relaxed">{t.garanti.para2}</p>
          </div>
        </div>
      </section>

      {/* Samarbejde og transparens */}
      <section className="bg-background text-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.samarbejde.heading}</h2>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.samarbejde.para1}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.samarbejde.para2}</p>
          </div>
          <Img
            src="/assets-photos/kontor-mia-malene.jpg"
            alt={t.samarbejde.heading}
            className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/3]"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Kompromisløs tilgang */}
      <section className="bg-card text-card-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <Img
            src="/assets-photos/dan-container.jpg"
            alt={t.sikkerhed.heading}
            className="rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/3]"
            sizes={SIZES.split}
          />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.sikkerhed.heading}</h2>
            <div className="mt-6 text-card-foreground/85 leading-relaxed space-y-4">
              <p>{t.sikkerhed.para1}</p>
              <p>{t.sikkerhed.para2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ledelsen */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center">{t.ledelse.heading}</h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((p, i) => (
              <div key={p.name} className="text-center">
                <Img src={p.img} alt={p.name} className="rounded-2xl shadow-lg w-full h-auto object-cover aspect-[3/4]" sizes={SIZES.card4} />
                <h3 className="mt-4 font-semibold text-lg">{p.name}</h3>
                <p className="text-sm opacity-80">{t.ledelse.roles[i]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MI Support IT */}
      <section className="relative text-white py-20 bg-black overflow-hidden">
        {/* Responsive/WebP background via <Img> instead of a CSS background-image */}
        <Img
          src="/wp-content/uploads/tastatur2_1400x1400.jpg"
          alt=""
          aria-hidden="true"
          sizes={SIZES.full}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/70" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-4 md:px-8 text-center flex flex-col items-center">
          <Img
            src="/wp-content/uploads/Logo-Maybe-FINAL-1.png"
            alt="MI Support IT logo"
            className="mb-6 h-auto w-80 md:w-96 max-w-full object-contain"
            sizes="(min-width: 768px) 384px, 320px"
          />
          <p className="leading-relaxed text-lg">{t.miit.body}</p>
          <a
            href="https://www.misupportit.dk/"
            className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-it-turquoise text-white px-9 py-4 text-base font-semibold transition-opacity hover:opacity-90"
          >
            <span>{t.miit.readMorePrefix}</span>
            <Img
              src="/wp-content/uploads/Logo-Maybe-FINAL-1.png"
              alt="MI Support IT"
              className="h-4 w-auto shrink-0 max-w-none object-contain translate-y-[2px]"
              sizes="100px"
            />
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Digitale værktøjer */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.digitale.heading}</h2>
            <p className="mt-6 leading-relaxed">{t.digitale.para1}</p>
            <p className="mt-4 leading-relaxed">{t.digitale.para2}</p>
          </div>
          <Img
            src="/assets-photos/digitale-vaerktoejer.jpg"
            alt={t.digitale.heading}
            sizes={SIZES.split}
            className="w-full h-auto rounded-2xl object-cover"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 leading-relaxed">{t.cta.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+4555551212"
              className="inline-flex items-center gap-2 rounded-2xl border-2 border-secondary bg-transparent text-secondary px-9 py-4 text-base font-semibold transition-colors hover:bg-secondary hover:text-white"
            >
              {t.cta.call}
            </a>
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.cta.write} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="bg-background text-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center">{t.blog.heading}</h2>
          <p className="mt-4 text-card-foreground/80 max-w-2xl mx-auto text-center">{t.blog.intro}</p>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {latestPosts.map((p) => {
              const c = p.content[locale]!;
              return (
                <Link
                  key={p.id}
                  to={articlePath(p, locale)}
                  className="group rounded-2xl overflow-hidden bg-card/15 flex flex-col hover:shadow-xl transition-shadow"
                >
                  <Img src={p.heroImage} alt={c.heroAlt} className="w-full h-64 object-cover" sizes={SIZES.split} />
                  <div className="p-8">
                    <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">{c.title}</h3>
                    <p className="mt-3 text-foreground/80 line-clamp-2">{c.description}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      {t.blog.readMore} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
